/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import * as yup from "yup";
import invert from "lodash/invert";
import Stepper from "@material-ui/core/Stepper";
import StepButton from "@material-ui/core/StepButton";
import Step from "@material-ui/core/Step";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { FormikProvider, useFormik, useFormikContext } from "formik";
import Start from "./steps/Start";
import SaveButtons from "../SaveButtons";
import Requirements from "./steps/Requirements";
import Loader from "../shared/Loader";
import { useAppContext } from "../../api/AppContext";
import { isEmpty, pickBy } from "lodash";
import { getFieldProps } from "../utils";
import InitialInformation from "./steps/NewsInitialInformation";
import NewsText from "./steps/NewsText";
import KeyPoints from "./steps/NewsKeyPoints";
import Publish from "./steps/Publish";
import { DatePicker } from "@material-ui/pickers";
import { useSnackbar } from "notistack";
import {
  getPreferredLanguageFromUser,
  getValidLanguagesFromUser,
  isNewsLanguageCompleteForSave,
} from "../shared/languageConfig";

const steps = [
  "Let's get started",
  "Access criteria to the news",
  "Initial information about the news",
  "News text",
  "Key points",
  "Confirm and publish",
];

const tabs = {
  "first-information": 2,
  "key-points": 4,
  "news-text": 3,
  publish: 5,
  requirement: 1,
  start: 0,
};

const textPrimary =
  "Hello, In this section you will be able to post a news about the CopilotGo!";

const textInfo =
  "To create a news story you must have the following information:";

const textConditions = [
  "Who can see it?",
  "The text of the news, with title and subtitle",
  "The summarized key points",
  "Image and optional Video",
];

const previewDomainQA = "https://ud.gniripsnitibah.net/news";
const previewDomainProd = "https://ud.habitinspiring.com/news";

const CustomStartStep = ({ textPrimary, textInfo, textConditions }) => {
  const formik = useFormikContext();

  const handleDateChange = useCallback(
    (field, value) => {
      formik.setFieldValue(field, value);
      formik.setFieldTouched(field);
    },
    [formik]
  );

  return (
    <>
      <Start
        textConditions={textConditions}
        textInfo={textInfo}
        textPrimary={textPrimary}
      />
      <Grid container spacing={2}>
        <Grid item lg={3} md={3} xs={12}>
          <DatePicker
            autoOk
            disablePast
            fullWidth
            ampm={false}
            format="dd MMMM yyyy"
            inputVariant="outlined"
            label={"Start date"}
            {...getFieldProps(formik, { name: "startdate" })}
            InputLabelProps={{
              shrink: true,
              style: { pointerEvents: "all" },
            }}
            maxDate={formik.values.expirationdate}
            maxDateMessage="Start date should not be after expiry date"
            variant="inline"
            onChange={(val) => handleDateChange("startdate", val)}
          />
        </Grid>
        <Grid item lg={3} md={3} xs={12}>
          <DatePicker
            autoOk
            disablePast
            fullWidth
            ampm={false}
            format="dd MMMM yyyy"
            inputVariant="outlined"
            label={"Expiry date"}
            {...getFieldProps(formik, { name: "expirationdate" })}
            InputLabelProps={{
              shrink: true,
              style: { pointerEvents: "all" },
            }}
            minDate={formik.values.startdate}
            minDateMessage="Date should not be before start date"
            value={formik.values.expirationdate}
            variant="inline"
            onChange={(val) => handleDateChange("expirationdate", val)}
          />
        </Grid>
      </Grid>
    </>
  );
};

const News = ({ tab, isNew }) => {
  const formik = useFormikContext();
  const [activeStep, setActiveStep] = useState(tabs[tab] ?? 0);
  const history = useHistory();
  const match = useRouteMatch();

  useEffect(() => {
    if (tab && tabs[tab] !== undefined) {
      setActiveStep(tabs[tab]);
    }
  }, [tab]);

  const handleStep = useCallback(
    (step) => () => {
      const invertedTabs = invert(tabs);
      const newPath = `/news/news/${isNew ? "new" : match.params.id}/${
        invertedTabs[step]
      }`;
      history.replace(newPath);
      setActiveStep(step);
    },
    [history, isNew, match.params.id]
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={4}>
            <CustomStartStep
              textConditions={textConditions}
              textInfo={textInfo}
              textPrimary={textPrimary}
            />
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={4}>
            <Requirements formik={formik} />
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={4}>
            <InitialInformation />
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={4}>
            <NewsText />
          </Grid>
        );
      case 4:
        return (
          <Grid container spacing={4}>
            <KeyPoints />
          </Grid>
        );
      case 5:
        return (
          <Grid container spacing={4}>
            <Publish
              buttonText={"Click to see the News Preview"}
              previewLink={
                process.env.REACT_APP_DASHBOARD_STAGE === "qa"
                  ? previewDomainQA
                  : previewDomainProd
              }
              title={
                "Congratulations!!!. Before previewing the news, make sure to save it."
              }
            />
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Stepper nonLinear activeStep={activeStep}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepButton color="inherit" onClick={handleStep(index)}>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Grid>

      <Grid item xs={12}>
        <div style={{ padding: "16px" }}>{renderStepContent(activeStep)}</div>
      </Grid>

      <Grid item xs={12}>
        {activeStep === steps.length - 1 ? (
          <Grid item xs={12}>
            <SaveButtons
              formik={formik}
              isNew={isNew}
              isShowSaveErrors={true}
            />
          </Grid>
        ) : (
          <Button
            color="primary"
            style={{ marginTop: "1rem" }}
            variant="outlined"
            onClick={handleStep(activeStep + 1)}
          >
            Next
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

const NewsContainer = () => {
  const match = useRouteMatch();
  const { tab, id } = match.params;
  const isNew = id === "new";
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const {
    copilotNews,
    createCopilotNews,
    updateCopilotNews,
    fetchCopilotNews,
    user,
  } = useAppContext();
  const [isLoading, setIsLoading] = useState(true);
  const [news, setNews] = useState(null);
  const validLanguages = useMemo(() => getValidLanguagesFromUser(user), [user]);
  const preferredLanguage = useMemo(
    () => getPreferredLanguageFromUser(user, validLanguages),
    [user, validLanguages]
  );

  useEffect(() => {
    fetchCopilotNews();
  }, [fetchCopilotNews]);

  useEffect(() => {
    if (!id || isNew) {
      setIsLoading(false);
      return;
    }
    const newsData = copilotNews.find((news) => news.id === id);
    if (newsData) {
      const transformedNews = {
        ...newsData,
        startdate: new Date(newsData.startdate),
        expirationdate: new Date(newsData.expirationdate),
        tagIds: (newsData.tags || []).map((t) => t.id),
        text_default: newsData.text_default || {
          title: "",
          subtitle: "",
          text: "",
          keyPoints: [],
        },
        text_en: newsData.text_en || {
          title: "",
          subtitle: "",
          text: "",
          keyPoints: [],
        },
        text_fr: newsData.text_fr || {
          title: "",
          subtitle: "",
          text: "",
          keyPoints: [],
        },
        text_es: newsData.text_es || {
          title: "",
          subtitle: "",
          text: "",
          keyPoints: [],
        },
        uiActiveSecondaryLanguage:
          validLanguages.find((language) => language !== preferredLanguage) ||
          null,
        uiPreferredLanguage: preferredLanguage,
        validLanguages,
        conditions: {
          allowSubscription: newsData.conditions?.allowSubscription || false,
          assignedField: newsData.conditions?.assignedField || "",
          cid: newsData.conditions?.cid || [],
          journey: newsData.conditions?.journey || [],
          language: newsData.conditions?.language || "",
          homeDisplay: newsData.conditions?.homeDisplay || false,
          show_lists: newsData.conditions?.show_lists || [],
          show_dids: Array.isArray(newsData.conditions?.show_dids)
            ? newsData.conditions.show_dids
            : [],
        },
      };
      setNews(transformedNews);
    }
  }, [copilotNews, id, isNew, preferredLanguage, validLanguages]);

  useEffect(() => {
    setIsLoading(false);
  }, [news]);

  const defaultStartDate = new Date();
  defaultStartDate.setHours(24, 0, 0, 0);
  const defaultExpirationDate = new Date(defaultStartDate.getTime());
  defaultExpirationDate.setHours(defaultExpirationDate.getHours() + 23, 59);

  const initialValues = isNew
    ? {
        startdate: defaultStartDate,
        expirationdate: defaultExpirationDate,
        tagIds: [],
        text_default: {
          title: "",
          subtitle: "",
          text: "",
          keyPoints: [],
        },
        text_en: {
          title: "",
          subtitle: "",
          text: "",
          keyPoints: [],
        },
        text_fr: {
          title: "",
          subtitle: "",
          text: "",
          keyPoints: [],
        },
        text_es: {
          title: "",
          subtitle: "",
          text: "",
          keyPoints: [],
        },
        uiActiveSecondaryLanguage:
          validLanguages.find((language) => language !== preferredLanguage) ||
          null,
        uiPreferredLanguage: preferredLanguage,
        validLanguages,
        content: [],
        image: null,
        conditions: {
          allowSubscription: false,
          assignedField: "",
          cid: [],
          journey: [],
          language: "",
          homeDisplay: false,
          show_lists: [],
          show_dids: [],
        },
      }
    : news || {
        startdate: defaultStartDate,
        expirationdate: defaultExpirationDate,
        tagIds: [],
        text_default: {
          title: "",
          subtitle: "",
          text: "",
          keyPoints: [],
        },
        text_en: {
          title: "",
          subtitle: "",
          text: "",
          keyPoints: [],
        },
        text_fr: {
          title: "",
          subtitle: "",
          text: "",
          keyPoints: [],
        },
        text_es: {
          title: "",
          subtitle: "",
          text: "",
          keyPoints: [],
        },
        uiActiveSecondaryLanguage:
          validLanguages.find((language) => language !== preferredLanguage) ||
          null,
        uiPreferredLanguage: preferredLanguage,
        validLanguages,
        content: [],
        image: null,
        conditions: {
          allowSubscription: false,
          assignedField: "",
          cid: [],
          journey: [],
          language: "",
          homeDisplay: false,
          show_lists: [],
          show_dids: [],
        },
      };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: yup
      .object()
      .shape({
        startdate: yup.date().required("Start date is required"),
        expirationdate: yup.date().required("Expiration date is required"),
        text_default: yup.object().shape({
          title: yup.string(),
          subtitle: yup.string(),
          keyPoints: yup.array().of(yup.string()),
        }),
        text_en: yup.object().shape({
          title: yup.string(),
          subtitle: yup.string(),
          keyPoints: yup.array().of(yup.string()),
        }),
        text_fr: yup.object().shape({
          title: yup.string(),
          subtitle: yup.string(),
          keyPoints: yup.array().of(yup.string()),
        }),
        text_es: yup.object().shape({
          title: yup.string(),
          subtitle: yup.string(),
          keyPoints: yup.array().of(yup.string()),
        }),
        content: yup.array().min(1, "At least one content item is required"),
        image: yup.mixed().required("Image is required"),
        conditions: yup.object().shape({
          allowSubscription: yup.boolean(),
          assignedField: yup.string().when("allowSubscription", {
            is: true,
            then: yup
              .string()
              .required("Field assigned is required when subscribed"),
            otherwise: yup.string(),
          }),
          cid: yup
            .array()
            .of(yup.string())
            .min(1, "At least one CID is required"),
          journey: yup.array().of(yup.string()).required("Journey is required"),
          show_lists: yup.array().of(yup.string()),
          show_dids: yup.array().of(yup.string()),
        }),
      })
      .test(
        "at-least-one-language-news",
        "At least one language must have title, subtitle and one news block filled",
        (value) =>
          validLanguages.some((language) =>
            isNewsLanguageCompleteForSave(value, language)
          )
      ),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const newsData = {
          startdate: values.startdate.toISOString(),
          expirationdate: values.expirationdate.toISOString(),
          text_en: values.text_en,
          text_default: values.text_default,
          text_fr: values.text_fr,
          text_es: values.text_es,
          content: values.content,
          image: values.image,
          tagIds: values.tagIds,
          conditions: {
            cid: values.conditions.cid,
            journey: values.conditions.journey,
            allowSubscription: values.conditions.allowSubscription,
            assignedField: values.conditions.assignedField,
            homeDisplay: values.conditions.homeDisplay,
            show_lists: values.conditions.show_lists,
            show_dids: values.conditions.show_dids,
          },
        };

        if (isNew) {
          await createCopilotNews(newsData);
          enqueueSnackbar("News created successfully!", { variant: "success" });
        } else {
          await updateCopilotNews({ ...newsData, id });
          enqueueSnackbar("News updated successfully!", { variant: "success" });
        }
        history.push("/news");
      } catch (error) {
        console.error("Error saving news:", error);
        enqueueSnackbar("Error saving news", { variant: "error" });
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <FormikProvider value={formik}>
            <News isNew={isNew} tab={tab} />
          </FormikProvider>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewsContainer;
